/**
 * OQ-294 · Hướng dẫn đăng ký ứng dụng OAuth — MỘT LẦN cho cả hệ thống.
 *
 * ── Vì sao vẫn còn một trang hướng dẫn ────────────────────────────────────
 *
 * Chủ dự án muốn *"chỉ việc bấm kết nối và đăng nhập"*. Từ tài khoản thứ hai
 * trở đi thì đúng như vậy. Nhưng Dropbox và Google **không cho** gọi OAuth từ
 * một ứng dụng chưa đăng ký, nên bước tạo app không bỏ được.
 *
 * Khác biệt so với OQ-286: bước ấy từ **mỗi lần thêm kho** thành **một lần cho
 * cả hệ thống**. Bốn lệnh `curl` để đổi `code` lấy `refresh_token` biến mất
 * hoàn toàn — đó là phần khó nhất và cũng là phần máy làm được.
 *
 * Địa chỉ callback hiện ngay ở đây kèm nút chép: nó phải được dán nguyên văn
 * vào ô *Redirect URI* của nhà cung cấp, và sai một ký tự thì lỗi chỉ lộ ra ở
 * bước cuối cùng — sau khi người dùng đã đăng nhập và bấm Cho phép.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';

const BUOC: Record<'dropbox' | 'gdrive', string[]> = {
  dropbox: [
    'settings.oauthApp.dropbox1',
    'settings.oauthApp.dropbox2',
    'settings.oauthApp.dropbox3',
    'settings.oauthApp.dropbox4',
  ],
  gdrive: [
    'settings.oauthApp.gdrive1',
    'settings.oauthApp.gdrive2',
    'settings.oauthApp.gdrive3',
    'settings.oauthApp.gdrive4',
  ],
};

export function OAuthAppGuide(): JSX.Element {
  const t = useT();
  const { toast } = useToast();
  const [mo, setMo] = useState(false);
  const [tab, setTab] = useState<'dropbox' | 'gdrive'>('dropbox');

  /*
   * Địa chỉ callback do MÁY CHỦ nói ra, không dựng ở trình duyệt.
   *
   * `window.location.origin` sẽ khác `APP_URL` khi có proxy đứng trước, và
   * chính `APP_URL` mới là thứ máy chủ dùng lúc đổi `code`. Hai chuỗi lệch nhau
   * thì nhà cung cấp từ chối ở bước cuối với một thông điệp không nhắc gì tới
   * nguyên nhân.
   */
  const cauHinh = useQuery({
    queryKey: ['oauth-callback-url'],
    queryFn: () => api.get<{ callback: string }>('/settings/backups/oauth-callback'),
  });
  const callback = cauHinh.data?.callback ?? '';

  return (
    <div className="token-guide" data-od-id="oauth-app-guide">
      <button
        type="button"
        className={`token-guide-toggle ${mo ? 'dang-mo' : ''}`}
        aria-expanded={mo}
        onClick={() => setMo((truoc) => !truoc)}
      >
        <Icon name={mo ? 'info' : 'warn'} size={16} />
        <span>{mo ? t('settings.oauthApp.hide') : t('settings.oauthApp.show')}</span>
      </button>

      {mo ? (
        <div className="scope-note token-guide-body">
          <Icon name="key" size={17} />
          <div className="grow">
            <b>{t('settings.oauthApp.title')}</b>
            <p className="small muted">{t('settings.oauthApp.once')}</p>

            <div className="row" style={{ gap: 6, margin: '10px 0' }}>
              {(['dropbox', 'gdrive'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`btn btn-sm ${tab === p ? 'btn-primary' : ''}`}
                  onClick={() => setTab(p)}
                  data-od-id={`oauth-guide-tab-${p}`}
                >
                  {p === 'dropbox' ? 'Dropbox' : 'Google Drive'}
                </button>
              ))}
            </div>

            <p className="token-guide-foot">
              <b>{t('settings.oauthApp.redirectUri')}</b>
            </p>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <code className="mono grow" data-od-id="oauth-callback-url">
                {callback || '…'}
              </code>
              <button
                className="btn btn-sm"
                type="button"
                disabled={!callback}
                onClick={() => {
                  void navigator.clipboard?.writeText(callback);
                  toast(t('common.copied'));
                }}
                data-od-id="oauth-callback-copy"
              >
                <Icon name="copy" size={14} />
                {t('common.copy')}
              </button>
            </div>

            <ol className="token-guide-steps" style={{ marginTop: 12 }}>
              {BUOC[tab].map((khoa) => (
                <li key={khoa}>{t(khoa).replace(/\*\*/g, '')}</li>
              ))}
            </ol>

            <p className="token-guide-foot">{t('settings.oauthApp.thenConnect')}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
